/**
 * CMS-05 — artists table plus the right-hand editing drawer. Filters (type, MBID, order) and
 * paging are server-side; deleting an artist that still has songs is refused by the API with
 * `409 entity_in_use` and the drawer says so up front (docs/06 §2.4).
 */
import type {
  ArtistKind,
  CmsArtistDTO,
  CmsArtistsQueryInput,
  CmsArtistsResponse,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Drawer } from '../components/ui/drawer';
import { Field, Input, Label, Select } from '../components/ui/field';
import { Badge } from '../components/ui/panel';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatNumber } from '../lib/format';
import { uploadFile } from '../lib/upload';

export interface ArtistFilters {
  q: string;
  kind: ArtistKind | '';
  mbid: 'yes' | 'no' | '';
}

export const EMPTY_ARTIST_FILTERS: ArtistFilters = { q: '', kind: '', mbid: '' };

const SORT_IDS = new Set(['name', 'relevance', 'song_count', 'created_at', 'updated_at']);

export function toArtistsQuery(
  filters: ArtistFilters,
  page: number,
  sorting: SortingState,
): CmsArtistsQueryInput {
  const query: CmsArtistsQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.kind) query.kind = filters.kind;
  if (filters.mbid) query.mbid = filters.mbid;
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

interface DrawerDraft {
  name: string;
  slug: string;
  kind: ArtistKind;
  mbid: string;
  relevance: number;
  imagePath: string | null;
}

function ArtistDrawer({ artist, onClose }: { artist: CmsArtistDTO | 'new'; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const isNew = artist === 'new';
  const current = isNew ? null : artist;
  // The parent mounts one drawer per row (`key`), so the draft is initial state, not synced state.
  const [draft, setDraft] = useState<DrawerDraft>(() => ({
    name: current?.name ?? '',
    slug: current?.slug ?? '',
    kind: current?.kind ?? 'artist',
    mbid: current?.mbid ?? '',
    relevance: current?.relevance ?? 0,
    imagePath: current?.imagePath ?? null,
  }));
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cms', 'artists'] });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: draft.name.trim(),
        kind: draft.kind,
        mbid: draft.mbid.trim() ? draft.mbid.trim() : null,
        relevance: draft.relevance,
        imagePath: draft.imagePath,
      };
      if (isNew) return api.cmsArtistCreate({ body });
      return api.cmsArtistPatch({ params: { id: current!.id }, body });
    },
    onSuccess: () => {
      void invalidate();
      onClose();
    },
    onError: () => setError(t('common:states.error')),
  });

  const confirm = useConfirm();

  const remove = useMutation({
    mutationFn: () => api.cmsArtistDelete({ params: { id: current!.id } }),
    onSuccess: () => {
      void invalidate();
      onClose();
    },
    onError: () => setError(t('cms:artists.deleteBlocked')),
  });

  // What the operator just picked, shown right away (the saved URL only exists after Save).
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const onImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || isNew) return;
    try {
      const { key } = await uploadFile(file, {
        kind: 'image',
        owner: 'artist',
        ownerId: current!.id,
      });
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(file);
      });
      setDraft((d) => ({ ...d, imagePath: key }));
    } catch {
      setError(t('cms:songs.editor.uploadFailed'));
    }
  };

  const blocked = !isNew && current!.songCount > 0;

  /** Deleting an artist takes them out of the live catalog, so it asks first (UX-211). */
  const onDelete = async () => {
    const ok = await confirm({
      title: t('cms:confirm.deleteArtist.title', { name: current!.name }),
      body: t('cms:confirm.deleteArtist.body'),
      confirmLabel: t('cms:confirm.deleteArtist.confirm'),
      tone: 'danger',
    });
    if (ok) remove.mutate();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? t('cms:artists.create') : t('cms:artists.edit')}
      subtitle={isNew ? undefined : t('cms:artists.meta', { id: current!.id, slug: current!.slug })}
      footer={
        <>
          {!isNew && (
            <Button
              variant="link"
              size="sm"
              className="mr-auto text-danger"
              disabled={blocked || remove.isPending}
              onClick={() => void onDelete()}
            >
              {t('common:actions.delete')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={draft.name.trim().length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('common:actions.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!isNew && (
          <div className="flex items-start gap-3">
            {(preview ?? current!.imageUrl) ? (
              <img
                src={preview ?? current!.imageUrl ?? undefined}
                alt=""
                className="size-20 rounded-lg object-cover"
              />
            ) : (
              <span aria-hidden="true" className="size-20 rounded-lg bg-canvas" />
            )}
            <div>
              <p className="text-[12px] font-semibold text-ink">{t('cms:artists.sourceImage')}</p>
              <p className="text-[12px] text-muted">{t('cms:artists.imageHelp')}</p>
              <div className="mt-2 flex gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  className="hidden"
                  data-testid="artist-image-input"
                  onChange={(event) => void onImage(event)}
                />
                <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
                  {t('cms:artists.uploadImage')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPreview(null);
                    setDraft((d) => ({ ...d, imagePath: null }));
                  }}
                >
                  {t('cms:artists.removeImage')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <Field label={t('cms:artists.name')}>
          {(id) => (
            <Input
              id={id}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          )}
        </Field>
        {!isNew && (
          <>
            <Field label={t('cms:artists.nameNorm')}>
              {(id) => <Input id={id} value={current!.nameNorm} readOnly disabled />}
            </Field>
            <Field label={t('cms:artists.slug')}>
              {(id) => <Input id={id} value={draft.slug} readOnly disabled />}
            </Field>
          </>
        )}
        <Field label={t('cms:artists.kind')} help={t('cms:artists.kindHelp')}>
          {(id) => (
            <Select
              id={id}
              value={draft.kind}
              onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value as ArtistKind }))}
            >
              <option value="artist">{t('cms:artists.kindArtist')}</option>
              <option value="placeholder">{t('cms:artists.kindPlaceholder')}</option>
            </Select>
          )}
        </Field>
        <Field label={t('cms:artists.mbid')}>
          {(id) => (
            <Input
              id={id}
              value={draft.mbid}
              onChange={(e) => setDraft((d) => ({ ...d, mbid: e.target.value }))}
            />
          )}
        </Field>
        <Field label={t('cms:artists.relevance')}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              value={draft.relevance}
              onChange={(e) => setDraft((d) => ({ ...d, relevance: Number(e.target.value) || 0 }))}
            />
          )}
        </Field>

        {blocked && (
          <p className="rounded-lg bg-warn/12 px-3 py-2 text-[12px] text-warn-ink">
            {t('cms:artists.deleteBlockedCount', { count: current!.songCount })}
          </p>
        )}
        {error && (
          <p className="text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}

export function Artists() {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');

  const [filters, setFilters] = useState<ArtistFilters>(EMPTY_ARTIST_FILTERS);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'song_count', desc: true }]);
  const [selected, setSelected] = useState<CmsArtistDTO | 'new' | null>(null);

  const query = toArtistsQuery(filters, page, sorting);
  const result = useQuery<CmsArtistsResponse>({
    queryKey: ['cms', 'artists', query],
    queryFn: () => api.cmsArtists({ query }),
    placeholderData: keepPreviousData,
  });

  const rows = result.data?.items ?? [];

  const columns = useMemo<ColumnDef<CmsArtistDTO>[]>(
    () => [
      {
        id: 'name',
        header: t('cms:artists.title'),
        cell: ({ row }) => (
          <span className="flex items-center gap-3">
            {row.original.imageUrl ? (
              <img src={row.original.imageUrl} alt="" className="size-8 rounded-md object-cover" />
            ) : (
              <span aria-hidden="true" className="size-8 rounded-md bg-canvas" />
            )}
            <span className="min-w-0">
              <span className="block truncate font-semibold text-ink">{row.original.name}</span>
              <span className="block truncate text-[12px] text-muted">{row.original.slug}</span>
            </span>
            {row.original.kind === 'placeholder' && (
              <Badge tone="warn">{t('cms:artists.kindPlaceholder')}</Badge>
            )}
          </span>
        ),
      },
      {
        id: 'relevance',
        header: t('cms:artists.relevance'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.relevance}</span>,
      },
      {
        id: 'song_count',
        header: t('cms:artists.songCount'),
        cell: ({ row }) => (
          <span className="tabular-nums">{formatNumber(row.original.songCount, locale)}</span>
        ),
      },
      {
        id: 'mbid',
        header: t('cms:artists.mbid'),
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-muted">
            {row.original.mbid ? row.original.mbid.slice(0, 8) : t('cms:artists.mbidNone')}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  const csvColumns: CsvColumn<CmsArtistDTO>[] = [
    { header: 'id', value: (a) => a.id },
    { header: 'name', value: (a) => a.name },
    { header: 'slug', value: (a) => a.slug },
    { header: 'kind', value: (a) => a.kind },
    { header: 'mbid', value: (a) => a.mbid ?? '' },
    { header: 'relevance', value: (a) => a.relevance },
    { header: 'song_count', value: (a) => a.songCount },
  ];

  const update = (patch: Partial<ArtistFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title={t('cms:artists.title')}
        /**
         * No count until the query answers: `total ?? 0` claimed an empty collection for the
         * seconds the first page takes (QA 2026-09-15 UX-502, the UX-201 rule applied to the
         * screens it was missed on). An error degrades the count to "—".
         */
        subtitle={
          result.data !== undefined
            ? t('cms:artists.subtitle', { total: formatNumber(result.data.total, locale) })
            : result.isError
              ? t('cms:artists.subtitle', { total: '—' })
              : t('cms:artists.subtitleLoading')
        }
        actions={
          canEdit ? (
            <Button onClick={() => setSelected('new')}>+ {t('cms:artists.new')}</Button>
          ) : null
        }
      />

      <div className="px-6 pb-8">
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          label={t('cms:artists.title')}
          total={result.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next);
            setPage(1);
          }}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={() => void result.refetch()}
          onRowClick={setSelected}
          rowLabel={(row) => t('cms:table.openRow', { name: row.name })}
          sortableIds={SORT_IDS}
          selectedRowId={selected && selected !== 'new' ? selected.id : null}
          csv={{
            filename: 'akapella-artists.csv',
            columns: csvColumns,
            // The whole filtered set, not the page on screen (CMS-22).
            rows: (onProgress) =>
              fetchAllRowsResult(
                (exportPage, pageSize) =>
                  api.cmsArtists({ query: { ...query, page: exportPage, pageSize } }),
                { onProgress },
              ),
          }}
          toolbar={
            <>
              <Input
                className="w-64"
                placeholder={t('cms:artists.searchPlaceholder')}
                aria-label={t('cms:artists.searchPlaceholder')}
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
              />
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-kind">{t('cms:artists.kind')}</Label>
                <Select
                  id="f-kind"
                  className="w-40"
                  value={filters.kind}
                  onChange={(e) => update({ kind: e.target.value as ArtistKind | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  <option value="artist">{t('cms:artists.kindArtist')}</option>
                  <option value="placeholder">{t('cms:artists.kindPlaceholder')}</option>
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-mbid">{t('cms:artists.mbid')}</Label>
                <Select
                  id="f-mbid"
                  className="w-40"
                  value={filters.mbid}
                  onChange={(e) => update({ mbid: e.target.value as ArtistFilters['mbid'] })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  <option value="yes">{t('cms:artists.hasMbid')}</option>
                  <option value="no">{t('cms:artists.noMbid')}</option>
                </Select>
              </span>
              <Button variant="link" size="sm" onClick={() => update(EMPTY_ARTIST_FILTERS)}>
                {t('cms:songs.clearFilters')}
              </Button>
            </>
          }
        />
      </div>

      {selected && canEdit && (
        <ArtistDrawer
          key={selected === 'new' ? 'new' : selected.id}
          artist={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
